import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import {
    OC, FONT, CATEGORY, CAT_KEYS, JOB_CATEGORIES, LOCATIONS, EMPLOYMENT_TYPES, WORKPLACE_TYPES,
    COMPANY_INDUSTRIES, COMPANY_SIZES, VERIFICATION_STATUS,
} from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import { isEmployerRole } from '../constants/index.js';
import OCApi from '../api/client.js';
import { Button, Badge, Icon, Input, Card, Toggle, CategoryIcon } from '../components/ui.js';
import { LoadingState, ErrorState, EmptyState } from '../components/AsyncState.js';
import { EmployerJobRow, EMPLOYER_STATUS_META } from './applications.js';

function EmployersScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [activeTab, setActiveTab] = useState('dashboard');

    useEffect(() => {
        if (s.route && s.route.tab) {
            const tab = s.route.tab;
            if (tab === 'profile') setActiveTab('profile');
            else if (tab === 'analytics') setActiveTab('analytics');
            else if (tab === 'applicants') setActiveTab('applicants');
            else if (tab === 'dashboard') setActiveTab('dashboard');
        }
    }, [s.route]);

    useEffect(() => {
        if (s.authed) s.loadEmployerDashboard();
    }, [s.authed]);

    const jobs = s.employerListings || [];
    const statsData = s.employerStats;
    const stats = [
        { label: 'Total Jobs', value: statsData?.byCategory?.jobs ?? jobs.filter(j => j.category === 'jobs').length, icon: 'briefcase', color: '#3B82F6' },
        { label: 'Internships', value: statsData?.byCategory?.internships ?? jobs.filter(j => j.category === 'internships').length, icon: 'cap', color: '#8B5CF6' },
        { label: 'Admissions', value: statsData?.byCategory?.admissions ?? jobs.filter(j => j.category === 'admissions').length, icon: 'building', color: '#14B8A6' },
        { label: 'Scholarships', value: statsData?.byCategory?.scholarships ?? jobs.filter(j => j.category === 'scholarships').length, icon: 'award', color: '#22C55E' },
        { label: 'Events', value: statsData?.byCategory?.events ?? jobs.filter(j => j.category === 'events').length, icon: 'spark', color: '#F97316' },
        { label: 'Active Posts', value: statsData?.byStatus?.live ?? jobs.filter(j => j.status === 'live').length, icon: 'check', color: '#22C55E' },
    ];

    const applicants = s.employerApplicants || [];

    const applicantStatusMeta = {
        applied: { label: 'Applied', color: OC.warning },
        review: { label: 'In review', color: '#3B82F6' },
        interview: { label: 'Interview', color: '#F59E0B' },
        offer: { label: 'Offer', color: OC.success },
        rejected: { label: 'Rejected', color: OC.neutral },
        withdrawn: { label: 'Withdrawn', color: OC.neutral },
        closed: { label: 'Closed', color: OC.neutral },
    };

    // 👇 SIDEBAR ITEMS
    const sidebarItems = [
        { key: 'dashboard', label: 'Dashboard', icon: 'grid' },
        { key: 'analytics', label: 'Analytics', icon: 'chart' },
        { key: 'applicants', label: 'Applicants', icon: 'user' },
        { key: 'profile', label: 'Company Profile', icon: 'building' },
    ];

    // ========================================
    // Hook: measure a container's real pixel width
    // ========================================
    const useMeasuredWidth = () => {
        const ref = useRef(null);
        const [width, setWidth] = useState(600);
        useEffect(() => {
            if (!ref.current) return;
            const el = ref.current;
            const ro = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const w = entry.contentRect.width;
                    if (w > 0) setWidth(w);
                }
            });
            ro.observe(el);
            setWidth(el.getBoundingClientRect().width || 600);
            return () => ro.disconnect();
        }, []);
        return [ref, width];
    };

    // ========================================
    // PROFESSIONAL LINE GRAPH — pixel-based coords
    // ========================================
    const LineGraph = ({ data, labels, color, height = 220, label, subtitle, valuePrefix = '', valueSuffix = '' }) => {
        const [containerRef, width] = useMeasuredWidth();

        const maxVal = Math.max(...data);
        const minVal = 0;
        const range = maxVal - minVal || 1;
        const padding = { top: 25, bottom: 20, left: 44, right: 20 };

        const graphWidth = Math.max(width - padding.left - padding.right, 1);
        const graphHeight = Math.max(height - padding.top - padding.bottom, 1);

        const xAt = i => padding.left + (i / (data.length - 1)) * graphWidth;
        const yAt = val => padding.top + graphHeight - ((val - minVal) / range) * graphHeight;

        const points = data.map((val, i) => `${xAt(i)},${yAt(val)}`).join(' ');

        // Y-axis labels
        const yLabels = [];
        const step = range / 5;
        for (let i = 0; i <= 5; i++) {
            yLabels.push(Math.round(minVal + step * i));
        }

        const gradId = `area-grad-${color.replace('#', '')}`;

        return React.createElement('div', { style: { width: '100%' } },
            // Header
            React.createElement('div', { style: {
                display: 'flex', justifyContent: 'space-between',
                marginBottom: 16, alignItems: 'flex-start'
            } },
                React.createElement('div', null,
                    React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: t.text.primary } }, label),
                    subtitle ? React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 2 } }, subtitle) : null
                ),
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
                    React.createElement('div', { style: { textAlign: 'right' } },
                        React.createElement('div', { style: { fontSize: 20, fontWeight: 800, color: t.text.primary } },
                            valuePrefix + data.reduce((a, b) => a + b, 0) + valueSuffix
                        ),
                        React.createElement('div', { style: { fontSize: 11, color: t.text.secondary } }, 'Total')
                    ),
                    React.createElement('div', { style: {
                        padding: '4px 12px',
                        borderRadius: 20,
                        background: data[data.length - 1] > data[0] ? (s.theme === 'dark' ? 'rgba(34,197,94,.15)' : '#D1FAE5') : (s.theme === 'dark' ? 'rgba(239,68,68,.15)' : '#FEE2E2'),
                        fontSize: 13,
                        fontWeight: 700,
                        color: data[data.length - 1] > data[0] ? OC.success : OC.urgent
                    } },
                        `${((data[data.length - 1] - data[0]) / (data[0] || 1) * 100).toFixed(1)}%`
                    )
                )
            ),

            // Graph
            React.createElement('div', { ref: containerRef, style: { position: 'relative', height, width: '100%' } },
                React.createElement('svg', {
                    width: '100%',
                    height: height,
                    viewBox: `0 0 ${width} ${height}`,
                    style: { display: 'block' }
                },
                    // Grid lines + Y-axis numbers
                    [0, 1, 2, 3, 4, 5].map(idx => {
                        const yPos = padding.top + (idx / 5) * graphHeight;
                        const yVal = yLabels[5 - idx];
                        return React.createElement('g', { key: idx },
                            React.createElement('line', {
                                x1: padding.left,
                                y1: yPos,
                                x2: width - padding.right,
                                y2: yPos,
                                stroke: t.border.base,
                                strokeWidth: 1,
                                strokeDasharray: '4,4'
                            }),
                            React.createElement('text', {
                                x: padding.left - 10,
                                y: yPos + 4,
                                textAnchor: 'end',
                                fontSize: 10,
                                fill: t.text.secondary,
                                fontFamily: FONT.mono,
                                fontWeight: 500
                            }, yVal)
                        );
                    }),

                    // Area under line
                    React.createElement('defs', null,
                        React.createElement('linearGradient', {
                            id: gradId,
                            x1: '0', y1: '0', x2: '0', y2: '1'
                        },
                            React.createElement('stop', { offset: '0%', stopColor: color, stopOpacity: 0.2 }),
                            React.createElement('stop', { offset: '100%', stopColor: color, stopOpacity: 0.02 })
                        )
                    ),
                    React.createElement('polygon', {
                        points: `${padding.left},${padding.top + graphHeight} ${points} ${width - padding.right},${padding.top + graphHeight}`,
                        fill: `url(#${gradId})`,
                        stroke: 'none'
                    }),

                    // Main line
                    React.createElement('polyline', {
                        points: points,
                        fill: 'none',
                        stroke: color,
                        strokeWidth: 3,
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round'
                    }),

                    // Data points + value labels
                    data.map((val, i) => {
                        const x = xAt(i);
                        const y = yAt(val);
                        return React.createElement('g', { key: i },
                            React.createElement('circle', { cx: x, cy: y, r: 9, fill: color, opacity: 0.08 }),
                            React.createElement('circle', { cx: x, cy: y, r: 4.5, fill: color, stroke: t.bg.page, strokeWidth: 2 }),
                            React.createElement('text', {
                                x: x,
                                y: y - 14,
                                textAnchor: 'middle',
                                fontSize: 10,
                                fill: t.text.secondary,
                                fontFamily: FONT.mono,
                                fontWeight: 600,
                                opacity: 0.9
                            }, val)
                        );
                    })
                )
            ),

            // X-axis labels
            React.createElement('div', {
                style: {
                    display: 'flex', justifyContent: 'space-between',
                    marginTop: 8, padding: `0 ${padding.right}px 0 ${padding.left}px`
                }
            },
                labels.map((lbl, i) =>
                    React.createElement('span', {
                        key: i,
                        style: { fontSize: 11, color: t.text.secondary, fontWeight: 500 }
                    }, lbl)
                )
            )
        );
    };

    // ========================================
    // MULTI-LINE GRAPH (Category-wise)
    // ========================================
    const MultiLineGraph = ({ data, labels, colors, height = 260, label }) => {
        const [containerRef, width] = useMeasuredWidth();

        const allValues = Object.values(data).flat();
        const maxVal = Math.max(...allValues);
        const minVal = 0;
        const range = maxVal - minVal || 1;
        const padding = { top: 25, bottom: 20, left: 44, right: 20 };

        const graphWidth = Math.max(width - padding.left - padding.right, 1);
        const graphHeight = Math.max(height - padding.top - padding.bottom, 1);

        const yLabels = [];
        const step = range / 5;
        for (let i = 0; i <= 5; i++) {
            yLabels.push(Math.round(minVal + step * i));
        }

        const categoryLabels = {
            jobs: 'Jobs',
            internships: 'Internships',
            scholarships: 'Scholarships',
            admissions: 'Admissions',
            events: 'Events'
        };

        const xAt = (i, len) => padding.left + (i / (len - 1)) * graphWidth;
        const yAt = val => padding.top + graphHeight - ((val - minVal) / range) * graphHeight;

        const datasets = Object.keys(data).map(key => {
            const series = data[key];
            const points = series.map((val, i) => `${xAt(i, series.length)},${yAt(val)}`).join(' ');
            return { key, points, color: colors[key], label: categoryLabels[key] };
        });

        return React.createElement('div', { style: { width: '100%' } },
            // Header
            React.createElement('div', {
                style: {
                    display: 'flex', justifyContent: 'space-between',
                    marginBottom: 16, alignItems: 'center'
                }
            },
                React.createElement('div', null,
                    React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: t.text.primary } }, label),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 2 } }, 'Category-wise trend over time')
                ),
                React.createElement('div', { style: { display: 'flex', gap: 14, flexWrap: 'wrap' } },
                    datasets.map(ds =>
                        React.createElement('span', {
                            key: ds.key,
                            style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: t.text.secondary, fontWeight: 500 }
                        },
                            React.createElement('span', {
                                style: { display: 'inline-block', width: 16, height: 3, borderRadius: 2, background: ds.color }
                            }),
                            ds.label
                        )
                    )
                )
            ),

            // Graph
            React.createElement('div', { ref: containerRef, style: { position: 'relative', height, width: '100%' } },
                React.createElement('svg', {
                    width: '100%',
                    height: height,
                    viewBox: `0 0 ${width} ${height}`,
                    style: { display: 'block' }
                },
                    // Grid lines
                    [0, 1, 2, 3, 4, 5].map(idx => {
                        const yPos = padding.top + (idx / 5) * graphHeight;
                        const yVal = yLabels[5 - idx];
                        return React.createElement('g', { key: idx },
                            React.createElement('line', {
                                x1: padding.left,
                                y1: yPos,
                                x2: width - padding.right,
                                y2: yPos,
                                stroke: t.border.base,
                                strokeWidth: 1,
                                strokeDasharray: '4,4'
                            }),
                            React.createElement('text', {
                                x: padding.left - 10,
                                y: yPos + 4,
                                textAnchor: 'end',
                                fontSize: 10,
                                fill: t.text.secondary,
                                fontFamily: FONT.mono,
                                fontWeight: 500
                            }, yVal)
                        );
                    }),

                    // Lines for each category
                    datasets.map(ds =>
                        React.createElement('polyline', {
                            key: ds.key,
                            points: ds.points,
                            fill: 'none',
                            stroke: ds.color,
                            strokeWidth: 2.5,
                            strokeLinecap: 'round',
                            strokeLinejoin: 'round'
                        })
                    ),

                    // Data points
                    datasets.map(ds =>
                        data[ds.key].map((val, i) => {
                            const x = xAt(i, data[ds.key].length);
                            const y = yAt(val);
                            return React.createElement('circle', {
                                key: `${ds.key}-${i}`,
                                cx: x,
                                cy: y,
                                r: 3.5,
                                fill: ds.color,
                                stroke: t.bg.page,
                                strokeWidth: 1.5
                            });
                        })
                    )
                )
            ),

            // X-axis labels
            React.createElement('div', {
                style: {
                    display: 'flex', justifyContent: 'space-between',
                    marginTop: 8, padding: `0 ${padding.right}px 0 ${padding.left}px`
                }
            },
                labels.map((lbl, i) =>
                    React.createElement('span', {
                        key: i,
                        style: { fontSize: 11, color: t.text.secondary, fontWeight: 500 }
                    }, lbl)
                )
            )
        );
    };

    // ========================================
    // PIE CHART
    // ========================================
    const PieChart = ({ data, size = 200 }) => {
        const total = data.reduce((sum, d) => sum + d.value, 0);
        let startAngle = -90;

        return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 32 } },
            React.createElement('svg', {
                width: size, height: size,
                style: { transform: 'rotate(-90deg)' }
            },
                data.map((item, index) => {
                    const percentage = (item.value / total) * 360;
                    const endAngle = startAngle + percentage;
                    const startRad = (startAngle * Math.PI) / 180;
                    const endRad = (endAngle * Math.PI) / 180;
                    const x1 = size / 2 + (size / 2 - 10) * Math.cos(startRad);
                    const y1 = size / 2 + (size / 2 - 10) * Math.sin(startRad);
                    const x2 = size / 2 + (size / 2 - 10) * Math.cos(endRad);
                    const y2 = size / 2 + (size / 2 - 10) * Math.sin(endRad);
                    const largeArc = percentage > 180 ? 1 : 0;
                    const path = `M ${size / 2} ${size / 2} L ${x1} ${y1} A ${size / 2 - 10} ${size / 2 - 10} 0 ${largeArc} 1 ${x2} ${y2} Z`;

                    const result = React.createElement('path', {
                        key: index,
                        d: path,
                        fill: item.color,
                        stroke: t.bg.page,
                        strokeWidth: 2
                    });

                    startAngle = endAngle;
                    return result;
                })
            ),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
                data.map(item => {
                    const pct = ((item.value / total) * 100).toFixed(1);
                    return React.createElement('div', { key: item.label, style: { display: 'flex', alignItems: 'center', gap: 10 } },
                        React.createElement('span', {
                            style: {
                                display: 'inline-block',
                                width: 14, height: 14,
                                borderRadius: 4,
                                background: item.color
                            }
                        }),
                        React.createElement('span', { style: { fontSize: 13, color: t.text.primary, flex: 1 } }, item.label),
                        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary } }, `${pct}%`)
                    );
                })
            )
        );
    };

    // ========================================
    // RENDER DASHBOARD
    // ========================================
    const renderDashboard = () => {
        const profile = s.employerProfile;
        return React.createElement('div', null,
            profile && profile.verificationStatus === 'pending' ?
                React.createElement('div', {
                    style: {
                        background: s.theme === 'dark' ? 'rgba(245,158,11,.12)' : '#FEF3C7',
                        border: `1px solid ${OC.warning}`,
                        borderRadius: 12, padding: '16px 20px',
                        display: 'flex', alignItems: 'center', gap: 12,
                        marginBottom: 20
                    }
                },
                    React.createElement(Icon, { name: 'clock', size: 20, color: OC.warning }),
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: 600, color: t.text.primary } }, 'Company profile under review'),
                        React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, 'You can prepare listings, but posting unlocks after admin verification.')
                    )
                ) : null,
            profile && profile.verificationStatus === 'rejected' ?
                React.createElement('div', {
                    style: {
                        background: s.theme === 'dark' ? 'rgba(239,68,68,.12)' : '#FEE2E2',
                        border: `1px solid ${OC.urgent}`,
                        borderRadius: 12, padding: '16px 20px',
                        marginBottom: 20
                    }
                },
                    React.createElement('div', { style: { fontWeight: 600, color: t.text.primary, marginBottom: 4 } }, 'Company profile rejected'),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, profile.rejectionReason || 'Update your profile and resubmit for review.')
                ) : null,
            profile && profile.verificationStatus === 'verified' ?
                React.createElement('div', {
                    style: {
                        background: s.theme === 'dark' ? 'rgba(34,197,94,.12)' : '#D1FAE5',
                        border: `1px solid ${OC.success}`,
                        borderRadius: 12, padding: '16px 20px',
                        marginBottom: 20
                    }
                },
                    React.createElement('div', { style: { fontWeight: 600, color: t.text.primary } }, 'Company profile verified — you can post opportunities.')
                ) : null,
            !profile || !profile.submitted ?
                React.createElement('div', {
                    style: {
                        background: t.bg.card,
                        border: `1px dashed ${t.border.strong}`,
                        borderRadius: 12, padding: '16px 20px',
                        marginBottom: 20
                    }
                },
                    React.createElement('div', { style: { fontWeight: 600, color: t.text.primary, marginBottom: 4 } }, 'Complete your company profile'),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginBottom: 12 } }, 'Submit your company details for admin verification before posting.'),
                    React.createElement(Button, { kind: 'secondary', size: 'sm', theme: s.theme, onClick: () => setActiveTab('profile') }, 'Open company profile')
                ) : null,
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 } },
                stats.map(st =>
                    React.createElement(Card, { key: st.label, theme: s.theme, padding: 18 },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
                            React.createElement('div', {
                                style: {
                                    width: 42, height: 42, borderRadius: 10,
                                    background: s.theme === 'dark' ? `${st.color}26` : `${st.color}14`,
                                    color: st.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                                }
                            }, React.createElement(Icon, { name: st.icon, size: 18 })),
                            React.createElement('div', null,
                                React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 24, color: t.text.primary, lineHeight: 1.1 } }, st.value),
                                React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 2 } }, st.label)
                            )
                        )
                    )
                )
            ),
            React.createElement('div', { style: { textAlign: 'center', marginBottom: 24 } },
                React.createElement(Button, {
                    kind: 'primary', size: 'lg', theme: s.theme,
                    onClick: () => s.go('postJob'),
                    icon: React.createElement(Icon, { name: 'plus', size: 16 }),
                    style: {
                        opacity: profile && profile.verificationStatus === 'verified' ? 1 : 0.55,
                    }
                }, 'Post New Opportunity')
            ),
            jobs.length > 0 ?
                React.createElement(Card, { theme: s.theme, padding: 20 },
                    React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 12 } }, 'Recent Postings'),
                    jobs.slice(0, 3).map(job =>
                        React.createElement('div', { key: job.id, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${t.border.base}` } },
                            React.createElement('div', null,
                                React.createElement('div', { style: { fontWeight: 600, color: t.text.primary } }, job.title),
                                React.createElement('div', { style: { fontSize: 12, color: t.text.secondary } }, job.org, ' · ', job.location)
                            ),
                            React.createElement(Badge, { color: EMPLOYER_STATUS_META[job.status]?.color || OC.neutral, theme: s.theme },
                                EMPLOYER_STATUS_META[job.status]?.label || job.status
                            )
                        )
                    )
                ) :
                React.createElement('div', { style: { textAlign: 'center', padding: '48px 24px', border: `1px dashed ${t.border.strong}`, borderRadius: 18 } },
                    React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 18, color: t.text.primary, margin: '0 0 6px' } }, 'No postings yet'),
                    React.createElement('p', { style: { fontSize: 14, color: t.text.secondary } }, 'Post your first opportunity to get started.')
                )
        );
    };

    // ========================================
    // RENDER ANALYTICS - 3 GRAPHS
    // ========================================
    const renderAnalytics = () => {
        const monthLabels = statsData?.monthLabels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
        const applicationsData = statsData?.applicationsByMonth || monthLabels.map(() => 0);
        const listingsData = statsData?.listingsByMonth || monthLabels.map(() => 0);
        const categoryData = statsData?.listingsByCategoryMonth || {
            jobs: monthLabels.map(() => 0),
            internships: monthLabels.map(() => 0),
            scholarships: monthLabels.map(() => 0),
            admissions: monthLabels.map(() => 0),
            events: monthLabels.map(() => 0),
        };

        const categoryColors = {
            jobs: '#3B82F6',
            internships: '#8B5CF6',
            scholarships: '#22C55E',
            admissions: '#14B8A6',
            events: '#F97316'
        };

        const pieData = [
            { label: 'Jobs', value: statsData?.byCategory?.jobs ?? 0, color: '#3B82F6' },
            { label: 'Internships', value: statsData?.byCategory?.internships ?? 0, color: '#8B5CF6' },
            { label: 'Scholarships', value: statsData?.byCategory?.scholarships ?? 0, color: '#22C55E' },
            { label: 'Admissions', value: statsData?.byCategory?.admissions ?? 0, color: '#14B8A6' },
            { label: 'Events', value: statsData?.byCategory?.events ?? 0, color: '#F97316' },
        ].filter(item => item.value > 0);

        return React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 } },
                React.createElement('div', null,
                    React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 22, color: t.text.primary, margin: 0 } }, 'Analytics'),
                    React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '4px 0 0' } }, 'Real performance from your listings and applicants')
                ),
                React.createElement('span', { style: { fontSize: 13, color: t.text.secondary } }, '📅 Last 6 months')
            ),

            React.createElement(Card, { theme: s.theme, padding: 24, style: { marginBottom: 24 } },
                React.createElement(LineGraph, {
                    data: applicationsData,
                    labels: monthLabels,
                    color: OC.orange,
                    height: 220,
                    label: 'Applications Received',
                    subtitle: 'Applicants to your live listings'
                })
            ),

            React.createElement(Card, { theme: s.theme, padding: 24, style: { marginBottom: 24 } },
                React.createElement(LineGraph, {
                    data: listingsData,
                    labels: monthLabels,
                    color: '#3B82F6',
                    height: 220,
                    label: 'New Postings',
                    subtitle: 'Opportunities you submitted'
                })
            ),

            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 } },
                React.createElement(Card, { theme: s.theme, padding: 24 },
                    React.createElement(MultiLineGraph, {
                        data: categoryData,
                        labels: monthLabels,
                        colors: categoryColors,
                        height: 240,
                        label: 'Category-wise Distribution'
                    })
                ),
                React.createElement(Card, { theme: s.theme, padding: 24 },
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary, marginBottom: 4 } }, 'Postings Distribution'),
                        React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginBottom: 16 } }, 'By Category'),
                        pieData.length ?
                            React.createElement('div', { style: { display: 'flex', justifyContent: 'center' } },
                                React.createElement(PieChart, { data: pieData, size: 160 })
                            ) :
                            React.createElement('div', { style: { textAlign: 'center', color: t.text.secondary, fontSize: 13, padding: '24px 0' } }, 'No postings yet')
                    )
                )
            )
        );
    };

    // ========================================
    // RENDER APPLICANTS
    // ========================================
    const renderApplicants = () => {
        return React.createElement('div', null,
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 22, color: t.text.primary, marginBottom: 20 } }, 'Recent Applicants'),
            applicants.length ?
            React.createElement(Card, { theme: s.theme, padding: 0, style: { overflow: 'hidden' } },
                React.createElement('div', { style: { overflowX: 'auto' } },
                    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body } },
                        React.createElement('thead', null,
                            React.createElement('tr', { style: { background: t.bg.subtle, borderBottom: `1px solid ${t.border.base}` } },
                                ['Name', 'Email', 'Applied For', 'Date', 'Status', 'CV'].map(h =>
                                    React.createElement('th', { key: h, style: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: t.text.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' } }, h)
                                )
                            )
                        ),
                        React.createElement('tbody', null,
                            applicants.map(a => {
                                const meta = applicantStatusMeta[a.status] || applicantStatusMeta.applied;
                                return React.createElement('tr', { key: a.id, style: { borderBottom: `1px solid ${t.border.base}` } },
                                    React.createElement('td', { style: { padding: '12px 16px', fontWeight: 500, color: t.text.primary } }, a.name),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary } }, a.email),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.primary } }, a.appliedFor),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary } }, a.date),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement('select', {
                                            value: a.status,
                                            onChange: (e) => s.updateEmployerApplicantStatus(a.id, e.target.value),
                                            style: {
                                                padding: '6px 10px', borderRadius: 8, border: `1px solid ${t.border.base}`,
                                                background: t.bg.page, color: t.text.primary, fontFamily: FONT.body, fontSize: 13,
                                            }
                                        },
                                            ['applied', 'review', 'interview', 'offer', 'rejected', 'withdrawn', 'closed'].map(st =>
                                                React.createElement('option', { key: st, value: st }, (applicantStatusMeta[st] || applicantStatusMeta.applied).label)
                                            )
                                        ),
                                        React.createElement('div', { style: { marginTop: 6 } },
                                            React.createElement(Badge, { color: meta.color, theme: s.theme }, meta.label)
                                        ),
                                        a.coverLetter ? React.createElement('div', { style: { marginTop: 8, fontSize: 12, color: t.text.secondary, lineHeight: 1.45, maxWidth: 260 } }, a.coverLetter) : null
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        a.resumeUrl ? React.createElement(Button, {
                                            kind: 'secondary', size: 'sm', theme: s.theme,
                                            onClick: () => OCApi.downloadApplicationResume(a.id),
                                            icon: React.createElement(Icon, { name: 'download', size: 14 }),
                                        }, 'View CV') : React.createElement('span', { style: { color: t.text.secondary, fontSize: 12.5 } }, '—')
                                    )
                                );
                            })
                        )
                    )
                )
            ) :
            React.createElement('div', { style: { textAlign: 'center', padding: '48px 24px', border: `1px dashed ${t.border.strong}`, borderRadius: 18 } },
                React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: 0 } }, 'No applicants yet. Applications appear here once your listings are live.')
            )
        );
    };

    // ========================================
    // COMPANY PROFILE COMPONENT (FIXED - RENDER HOGA!)
    // ========================================
    const CompanyProfileComponent = () => {
        const profile = s.employerProfile || {};
        const [companyName, setCompanyName] = useState(profile.companyName || '');
        const [industry, setIndustry] = useState(profile.industry || '');
        const [companySize, setCompanySize] = useState(profile.companySize || '');
        const [headquarters, setHeadquarters] = useState(profile.headquarters || '');
        const [website, setWebsite] = useState(profile.website || '');
        const [email, setEmail] = useState(profile.email || s.user.email || '');
        const [phone, setPhone] = useState(profile.phone || '');
        const [about, setAbout] = useState(profile.about || '');
        const [isSubmitting, setIsSubmitting] = useState(false);

        useEffect(() => {
            if (!s.employerProfile) return;
            setCompanyName(s.employerProfile.companyName || '');
            setIndustry(s.employerProfile.industry || '');
            setCompanySize(s.employerProfile.companySize || '');
            setHeadquarters(s.employerProfile.headquarters || '');
            setWebsite(s.employerProfile.website || '');
            setEmail(s.employerProfile.email || s.user.email || '');
            setPhone(s.employerProfile.phone || '');
            setAbout(s.employerProfile.about || '');
        }, [s.employerProfile]);

        const industries = COMPANY_INDUSTRIES;
        const sizes = COMPANY_SIZES;
        const verifMeta = VERIFICATION_STATUS[profile.verificationStatus] || VERIFICATION_STATUS.pending;

        const handleSubmit = async () => {
            if (!companyName || !industry || !companySize || !headquarters || !email || !about) {
                s.showToast('Please fill in all required fields', 'x');
                return;
            }
            setIsSubmitting(true);
            try {
                await s.saveEmployerProfile({
                    companyName: companyName.trim(),
                    industry,
                    companySize,
                    headquarters: headquarters.trim(),
                    website: website.trim() || null,
                    email: email.trim(),
                    phone: phone.trim() || null,
                    about: about.trim(),
                });
            } catch (err) {
                s.showToast(err.message || 'Could not save profile', 'x');
            } finally {
                setIsSubmitting(false);
            }
        };

        return React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 } },
                React.createElement('div', null,
                    React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 22, color: t.text.primary, margin: 0 } }, 'Company Profile'),
                    React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '4px 0 0' } }, 'Complete your profile to get verified')
                ),
                React.createElement(Badge, { color: verifMeta.color, theme: s.theme, icon: React.createElement(Icon, { name: verifMeta.icon, size: 12 }) }, verifMeta.label)
            ),

            React.createElement(Card, { theme: s.theme, padding: 28 },
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 } },
                    // Company Name
                    React.createElement('div', { style: { gridColumn: '1 / -1' } },
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'Company Name *',
                            value: companyName,
                            onChange: setCompanyName,
                            placeholder: 'e.g. Google, Microsoft',
                            icon: 'building'
                        })
                    ),
                    
                    // Industry
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary, marginBottom: 7 } }, 'Industry *'),
                        React.createElement('select', {
                            value: industry,
                            onChange: e => setIndustry(e.target.value),
                            style: {
                                width: '100%', height: 44, padding: '0 13px', borderRadius: 10,
                                background: t.bg.page, border: `1.5px solid ${t.border.base}`,
                                fontFamily: FONT.body, fontSize: 14.5, color: t.text.primary,
                                outline: 'none', cursor: 'pointer'
                            }
                        },
                            React.createElement('option', { value: '' }, 'Select industry...'),
                            industries.map(ind => React.createElement('option', { key: ind, value: ind }, ind))
                        )
                    ),

                    // Company Size
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary, marginBottom: 7 } }, 'Company Size *'),
                        React.createElement('select', {
                            value: companySize,
                            onChange: e => setCompanySize(e.target.value),
                            style: {
                                width: '100%', height: 44, padding: '0 13px', borderRadius: 10,
                                background: t.bg.page, border: `1.5px solid ${t.border.base}`,
                                fontFamily: FONT.body, fontSize: 14.5, color: t.text.primary,
                                outline: 'none', cursor: 'pointer'
                            }
                        },
                            React.createElement('option', { value: '' }, 'Select size...'),
                            sizes.map(size => React.createElement('option', { key: size, value: size }, size, ' employees'))
                        )
                    ),

                    // Headquarters
                    React.createElement('div', { style: { gridColumn: '1 / -1' } },
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'Headquarters *',
                            value: headquarters,
                            onChange: setHeadquarters,
                            placeholder: 'City, Country',
                            icon: 'mapPin'
                        })
                    ),

                    // Website
                    React.createElement('div', null,
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'Company Website',
                            value: website,
                            onChange: setWebsite,
                            placeholder: 'https://example.com',
                            icon: 'globe'
                        })
                    ),

                    // Email
                    React.createElement('div', null,
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'Company Email *',
                            value: email,
                            onChange: setEmail,
                            placeholder: 'hr@company.com',
                            type: 'email',
                            icon: 'mail'
                        })
                    ),

                    // Phone
                    React.createElement('div', { style: { gridColumn: '1 / -1' } },
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'Phone Number',
                            value: phone,
                            onChange: setPhone,
                            placeholder: '+92 300 1234567',
                            icon: 'phone'
                        })
                    ),

                    // About
                    React.createElement('div', { style: { gridColumn: '1 / -1' } },
                        React.createElement(Input, {
                            theme: s.theme,
                            label: 'About Company *',
                            value: about,
                            onChange: setAbout,
                            as: 'textarea',
                            rows: 4,
                            placeholder: 'Tell applicants about your company...'
                        })
                    )
                ),

                // Submit Button
                React.createElement('div', { style: { display: 'flex', gap: 12, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${t.border.base}` } },
                    React.createElement(Button, {
                        kind: 'ghost', size: 'lg', theme: s.theme,
                        onClick: () => setActiveTab('dashboard'),
                        style: { flex: 1 }
                    }, 'Back to Dashboard'),
                    React.createElement(Button, {
                        kind: 'primary', size: 'lg', theme: s.theme,
                        onClick: handleSubmit,
                        style: {
                            flex: 2,
                            opacity: companyName && industry && companySize && headquarters && email && about ? 1 : 0.5,
                            cursor: companyName && industry && companySize && headquarters && email && about ? 'pointer' : 'not-allowed',
                            pointerEvents: companyName && industry && companySize && headquarters && email && about ? 'auto' : 'none'
                        },
                        icon: isSubmitting ? React.createElement(Icon, { name: 'clock', size: 16 }) : null
                    }, isSubmitting ? 'Submitting...' : 'Submit for Review')
                )
            )
        );
    };

    // ========================================
    // RENDER CONTENT
    // ========================================
    const renderContent = () => {
        switch(activeTab) {
            case 'dashboard':
                return renderDashboard();
            case 'analytics':
                return renderAnalytics();
            case 'applicants':
                return renderApplicants();
            case 'profile':
                return React.createElement(CompanyProfileComponent, null);
            default:
                return renderDashboard();
        }
    };

    // ========================================
    // MAIN RENDER
    // ========================================
    if (!s.authed) {
        return React.createElement('div', { style: { maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' } },
            React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, color: t.text.primary, marginBottom: 8 } }, 'Employer Dashboard'),
            React.createElement('p', { style: { color: t.text.secondary, marginBottom: 24 } }, 'Log in to manage your company profile, postings, and applicants.'),
            React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, onClick: () => s.go('login') }, 'Log in to continue')
        );
    }

    if (!isEmployerRole(s.user.role)) {
        return React.createElement('div', { style: { maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' } },
            React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, color: t.text.primary, marginBottom: 8 } }, 'Employer account required'),
            React.createElement('p', { style: { color: t.text.secondary, marginBottom: 24 } }, 'Switch to a hiring employer account in Settings to access this dashboard.'),
            React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, onClick: () => s.go('account') }, 'Go to Settings')
        );
    }

    if (s.employerLoading && !jobs.length && !s.employerProfile) {
        return React.createElement('div', { style: { padding: '80px 24px', textAlign: 'center', color: t.text.secondary } }, 'Loading employer dashboard...');
    }

    return React.createElement('div', { style: { display: 'flex', minHeight: 'calc(100vh - 68px)' } },
        // SIDEBAR
        React.createElement('aside', {
            style: {
                width: 240,
                background: t.bg.card,
                borderRight: `1px solid ${t.border.base}`,
                padding: '20px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                position: 'sticky',
                top: 68,
                height: 'calc(100vh - 68px)',
                overflowY: 'auto',
                flexShrink: 0,
            }
        },
            React.createElement('div', { style: { padding: '0 8px 16px', borderBottom: `1px solid ${t.border.base}`, marginBottom: 12 } },
                React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 16, color: t.text.primary } }, '☰ Employer Panel')
            ),
            sidebarItems.map(item => {
                const active = activeTab === item.key;
                return React.createElement('button', {
                    key: item.key,
                    onClick: () => setActiveTab(item.key),
                    style: {
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 10,
                        border: 'none', cursor: 'pointer', width: '100%',
                        background: active ? (s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted) : 'transparent',
                        color: active ? OC.orange : t.text.secondary,
                        fontFamily: FONT.body, fontWeight: active ? 600 : 500, fontSize: 14,
                        transition: 'all .15s ease',
                    },
                    onMouseEnter: e => {
                        if (!active) e.currentTarget.style.background = t.bg.subtle;
                    },
                    onMouseLeave: e => {
                        if (!active) e.currentTarget.style.background = 'transparent';
                    }
                },
                    React.createElement(Icon, { name: item.icon, size: 18, color: active ? OC.orange : t.text.secondary }),
                    item.label
                );
            })
        ),
        // MAIN CONTENT
        React.createElement('main', { style: { flex: 1, padding: '24px 32px', overflowY: 'auto' } },
            renderContent()
        )
    );
}

// --- CATEGORY_FIELD_CONFIG ---
const CATEGORY_FIELD_CONFIG = {
    jobs: [
        { key: 'jobCategory', label: 'Job category', type: 'select', options: JOB_CATEGORIES },
        { key: 'employmentType', label: 'Employment type', type: 'select', options: EMPLOYMENT_TYPES },
        { key: 'workplaceType', label: 'Workplace type', type: 'select', options: WORKPLACE_TYPES },
        { key: 'salaryType', label: 'Salary type', type: 'select', options: ['Fixed', 'Range', 'Negotiable'] },
        { key: 'salaryMin', label: 'Minimum salary', type: 'number' },
        { key: 'salaryMax', label: 'Maximum salary', type: 'number' },
        { key: 'currency', label: 'Currency', type: 'select', options: ['PKR', 'USD', 'AED', 'EUR'] },
        { key: 'experienceRequired', label: 'Experience required', type: 'text' },
        { key: 'educationRequired', label: 'Education required', type: 'text' },
        { key: 'requiredSkills', label: 'Required skills (comma separated)', type: 'text' },
        { key: 'responsibilities', label: 'Responsibilities', type: 'textarea' },
        { key: 'openings', label: 'Number of openings', type: 'number' },
        { key: 'applicationType', label: 'Application type', type: 'radio', options: ['Internal Apply', 'External Apply'] },
    ],
    internships: [
        { key: 'internshipCategory', label: 'Internship category', type: 'select', options: JOB_CATEGORIES },
        { key: 'internshipType', label: 'Internship type', type: 'select', options: ['Full-time', 'Part-time'] },
        { key: 'workplaceType', label: 'Workplace type', type: 'select', options: WORKPLACE_TYPES },
        { key: 'duration', label: 'Duration (e.g. 3 months)', type: 'text' },
        { key: 'stipendType', label: 'Stipend type', type: 'select', options: ['Paid', 'Unpaid'] },
        { key: 'stipendAmount', label: 'Monthly stipend', type: 'number' },
        { key: 'currency', label: 'Currency', type: 'select', options: ['PKR', 'USD', 'AED', 'EUR'] },
        { key: 'educationLevel', label: 'Education level', type: 'text' },
        { key: 'requiredSkills', label: 'Required skills (comma separated)', type: 'text' },
        { key: 'responsibilities', label: 'Responsibilities', type: 'textarea' },
        { key: 'certificate', label: 'Certificate provided', type: 'select', options: ['Yes', 'No'] },
        { key: 'ppo', label: 'PPO opportunity', type: 'select', options: ['Yes', 'No'] },
        { key: 'openings', label: 'Number of openings', type: 'number' },
        { key: 'applicationType', label: 'Application type', type: 'radio', options: ['Internal Apply', 'External Apply'] },
    ],
    scholarships: [
        { key: 'scholarshipCategory', label: 'Scholarship category', type: 'select', options: ['Merit', 'Need-Based', 'Research', 'International'] },
        { key: 'degreeLevel', label: 'Degree level', type: 'select', options: ['BS', 'MS', 'PhD'] },
        { key: 'country', label: 'Country', type: 'text' },
        { key: 'eligibility', label: 'Eligibility criteria', type: 'textarea' },
        { key: 'coverage', label: 'Scholarship coverage', type: 'select', options: ['Full funding', 'Partial funding'] },
    ],
    admissions: [
        { key: 'programName', label: 'Program name', type: 'text' },
        { key: 'institution', label: 'Institution', type: 'text' },
        { key: 'intake', label: 'Intake session', type: 'text' },
        { key: 'tuitionFee', label: 'Tuition fee', type: 'text' },
    ],
    events: [
        { key: 'eventCategory', label: 'Event category', type: 'select', options: ['Workshop', 'Seminar', 'Hackathon', 'Webinar', 'Career Fair', 'Competition'] },
        { key: 'eventType', label: 'Event type', type: 'select', options: ['Online', 'Offline', 'Hybrid'] },
        { key: 'startTime', label: 'Start time', type: 'time' },
        { key: 'endTime', label: 'End time', type: 'time' },
        { key: 'venue', label: 'Venue / meeting link', type: 'text' },
        { key: 'ticketType', label: 'Ticket type', type: 'select', options: ['Free', 'Paid'] },
        { key: 'ticketPrice', label: 'Ticket price (if paid)', type: 'number' },
        { key: 'registrationUrl', label: 'Registration URL', type: 'text' },
    ],
};

// --- PostOpportunityScreen ---
function PostOpportunityScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [category, setCategory] = useState(s.route.cat || 'jobs');
    const [core, setCore] = useState({ title: '', org: '', location: LOCATIONS[0], deadline: '', description: '', applyLink: '' });
    const [fields, setFields] = useState({});

    const changeCategory = (key) => { setCategory(key); setFields({}); };
    const setCoreField = (key, val) => setCore(c => ({ ...c, [key]: val }));
    const setExtraField = (key, val) => setFields(f => ({ ...f, [key]: val }));

    const needsApplyLink = category === 'scholarships' || category === 'admissions' ||
        ((category === 'jobs' || category === 'internships') && fields.applicationType === 'External Apply');
    const valid = core.title.trim().length > 2 && core.org.trim().length > 1 && core.description.trim().length > 10 && (needsApplyLink ? core.applyLink.trim().length > 4 : true);

    const submit = async () => {
        if (!valid) return;
        if (!s.authed) {
            s.showToast('Log in to post opportunities', 'x');
            s.go('login');
            return;
        }
        if (!isEmployerRole(s.user.role)) {
            s.showToast('Employer account required', 'x');
            s.go('account');
            return;
        }
        if (s.employerProfile?.verificationStatus !== 'verified') {
            s.showToast('Complete and verify your company profile first', 'x');
            s.go('employers', { tab: 'profile' });
            return;
        }
        try {
            const detailPayload = OCApi.buildDetailPayload(category, fields);
            await s.submitStagedOpportunity({
                title: core.title.trim(),
                organization: core.org.trim(),
                description: core.description.trim(),
                categorySlug: category,
                applyUrl: core.applyLink.trim(),
                isRemote: /remote/i.test(core.location),
                city: core.location,
                deadline: core.deadline || undefined,
                detailPayload,
            });
            await s.loadEmployerDashboard();
            s.showToast(`${CATEGORY[category].label.slice(0, -1)} submitted for review`, 'check');
            s.go('employers');
        } catch (err) {
            s.showToast(err.message || 'Could not submit opportunity', 'x');
        }
    };

    const selectRow = (label, value, onChange, options) =>
        React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary, marginBottom: 7 } }, label),
            React.createElement('select', {
                value: value || options[0], onChange: e => onChange(e.target.value),
                style: { width: '100%', height: 44, padding: '0 13px', borderRadius: 10, background: t.bg.page, border: `1.5px solid ${t.border.base}`, fontFamily: FONT.body, fontSize: 14.5, color: t.text.primary, outline: 'none', cursor: 'pointer' }
            }, options.map(o => React.createElement('option', { key: o, value: o }, o)))
        );

    const radioRow = (label, value, onChange, options) =>
        React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary, marginBottom: 9 } }, label),
            React.createElement('div', { style: { display: 'flex', gap: 10 } },
                options.map(o => {
                    const active = (value || options[0]) === o;
                    return React.createElement('button', {
                        key: o,
                        type: 'button',
                        onClick: () => onChange(o),
                        style: {
                            flex: 1, display: 'flex', alignItems: 'center', gap: 9,
                            padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                            border: `1.5px solid ${active ? OC.orange : t.border.base}`,
                            background: active ? (s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted) : t.bg.page,
                            fontFamily: FONT.body, fontSize: 14, fontWeight: active ? 700 : 500,
                            color: active ? OC.orange : t.text.primary,
                        }
                    },
                        React.createElement('span', {
                            style: {
                                width: 17, height: 17, borderRadius: '50%', flex: '0 0 auto',
                                border: `1.5px solid ${active ? OC.orange : t.border.strong}`,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }
                        }, active ? React.createElement('span', { style: { width: 8, height: 8, borderRadius: '50%', background: OC.orange } }) : null),
                        o
                    );
                })
            )
        );

    const extraConfig = CATEGORY_FIELD_CONFIG[category] || [];
    return React.createElement('div', { style: { width: '100%', padding: '0 40px' } },
        React.createElement('div', { style: { padding: '40px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text.secondary, marginBottom: 12 } },
                React.createElement('button', { onClick: () => s.go('employers'), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary, fontSize: 12.5, padding: 0 } }, 'Employer dashboard'),
                React.createElement(Icon, { name: 'chevRight', size: 12 }),
                React.createElement('span', { style: { color: t.text.primary, fontWeight: 600 } }, 'Post an opportunity')
            ),
            React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 30, margin: '0 0 28px', color: t.text.primary, letterSpacing: '-0.02em' } }, 'Post an opportunity'),

            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28, alignItems: 'start' } },
                React.createElement('aside', {
                    style: { background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 16, padding: 12, position: 'sticky', top: 88, display: 'flex', flexDirection: 'column', gap: 4 }
                },
                    CAT_KEYS.map(key => {
                        const active = category === key;
                        const cat = CATEGORY[key];
                        return React.createElement('button', {
                            key: key, onClick: () => changeCategory(key),
                            style: {
                                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 10,
                                border: 'none', cursor: 'pointer', textAlign: 'left',
                                background: active ? (s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted) : 'transparent',
                                color: active ? OC.orange : t.text.primary,
                                fontFamily: FONT.body, fontWeight: active ? 700 : 500, fontSize: 13.5,
                            }
                        }, React.createElement(Icon, { name: cat.icon, size: 16 }), cat.label);
                    })
                ),

                React.createElement('div', null,
                    React.createElement(Card, { theme: s.theme, padding: 24, style: { display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 18 } },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                            React.createElement(CategoryIcon, { category: category, size: 34, theme: s.theme }),
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary } }, CATEGORY[category].label, ' — core details')
                        ),
                        React.createElement(Input, { theme: s.theme, label: category === 'events' ? 'Event title' : 'Title', value: core.title, onChange: v => setCoreField('title', v), placeholder: `e.g. ${category === 'jobs' ? 'Senior Frontend Engineer' : category === 'events' ? 'AI Career Fair 2026' : 'Opportunity title'}`, icon: 'briefcase' }),
                        React.createElement(Input, { theme: s.theme, label: category === 'events' ? 'Organizer name' : (category === 'jobs' || category === 'internships') ? 'Company name' : 'Organization / institution', value: core.org, onChange: v => setCoreField('org', v), placeholder: 'Name', icon: 'building' }),
                        (category === 'jobs' || category === 'internships') ? selectRow('Location', core.location, v => setCoreField('location', v), LOCATIONS) : null,
                        category === 'events' ? React.createElement(Input, { theme: s.theme, label: 'Event date', value: core.deadline, onChange: v => setCoreField('deadline', v), type: 'date', icon: 'clock' })
                            : React.createElement(Input, { theme: s.theme, label: 'Application deadline', value: core.deadline, onChange: v => setCoreField('deadline', v), type: 'date', icon: 'clock' }),
                        (category === 'scholarships' || category === 'admissions') ? React.createElement(Input, { theme: s.theme, label: 'External application link', value: core.applyLink, onChange: v => setCoreField('applyLink', v), placeholder: 'https://…', icon: 'globe' }) : null
                    ),

                    React.createElement(Card, { theme: s.theme, padding: 24, style: { display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 18 } },
                        React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary } }, CATEGORY[category].label, ' details'),
                        extraConfig.map(f =>
                            f.type === 'select'
                                ? React.createElement(React.Fragment, { key: f.key }, selectRow(f.label, fields[f.key], v => setExtraField(f.key, v), f.options))
                                : f.type === 'radio'
                                ? React.createElement(React.Fragment, { key: f.key }, radioRow(f.label, fields[f.key], v => setExtraField(f.key, v), f.options))
                                : f.type === 'textarea'
                                ? React.createElement(Input, { key: f.key, theme: s.theme, label: f.label, value: fields[f.key] || '', onChange: v => setExtraField(f.key, v), as: 'textarea', rows: 4 })
                                : React.createElement(Input, { key: f.key, theme: s.theme, label: f.label, value: fields[f.key] || '', onChange: v => setExtraField(f.key, v), type: f.type === 'time' ? 'time' : f.type })
                        ),
                        (fields.applicationType === 'External Apply') ? React.createElement(Input, { theme: s.theme, label: 'External apply URL', value: core.applyLink, onChange: v => setCoreField('applyLink', v), placeholder: 'https://…', icon: 'globe' }) : null,
                        React.createElement(Input, { theme: s.theme, label: 'Description', value: core.description, onChange: v => setCoreField('description', v), as: 'textarea', rows: 6, placeholder: 'Details, requirements, benefits…' })
                    ),

                    React.createElement('div', { style: { display: 'flex', gap: 10 } },
                        React.createElement(Button, { kind: 'ghost', size: 'lg', theme: s.theme, onClick: () => s.go('employers'), style: { flex: 1 } }, 'Cancel'),
                        React.createElement(Button, {
                            kind: 'primary', size: 'lg', theme: s.theme, onClick: submit,
                            style: { flex: 2, opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'not-allowed', pointerEvents: valid ? 'auto' : 'none' }
                        }, 'Submit for review')
                    )
                )
            )
        )
    );
}

export { EmployersScreen, PostOpportunityScreen, CATEGORY_FIELD_CONFIG };
